import { Alert, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { FacilityWasteView, SubmissionStepType } from "generated-graphql/graphql";
import { useAdjustableStepWasteDateRange } from "hooks/useStepWasteDateRange";
import { DateTime } from "luxon";
import { Suspense } from "react";
import { useParams } from "react-router-dom";
import { TenantWasteFacilityProvider } from "routes/Customer/Waste/Facilities/Facility/TenantWasteFacilityProvider";
import {
  WasteStreamsBody,
  WasteStreamsDateRangeCaption,
  WasteStreamsInfoTooltip,
} from "routes/Customer/Waste/Facilities/Facility/WasteStreamsTile";

import { StepHeader } from "../StepHeader";
import { StepSubHeader } from "../StepSubHeader";
import { AmendInWasteOverviewLink } from "./AmendInWasteOverviewLink";
import { ReportableWasteCodes } from "./ReportableWasteCodes";
import { StepContinueFooter } from "./StepContinueFooter";
import { WasteGeneratorStatus } from "./WasteGeneratorStatus";

/**
 * Stepper-native activity-window control: a start/end date pair, kept narrow with
 * a `maxWidth`. Deliberately does NOT reuse the waste-overview `WasteDateRangePicker`
 * — that component's chrome (and the dashboard tiles' cards) made the step's
 * sections read as inconsistent, nested cards. Wraps its own `LocalizationProvider`
 * so it works wherever it's mounted.
 */
function ActivityDateRange({
  startDate,
  endDate,
  onChange,
}: {
  startDate: string;
  endDate: string;
  onChange: (next: { startDate?: string; endDate?: string }) => void;
}) {
  const start = startDate ? DateTime.fromISO(startDate) : null;
  const end = endDate ? DateTime.fromISO(endDate) : null;
  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Stack direction="row" spacing={2} sx={{ maxWidth: 440 }}>
        <DatePicker
          label="Start Date"
          value={start}
          format="MM/dd/yyyy"
          onChange={(next) => onChange({ startDate: next?.toISODate() ?? undefined, endDate })}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
        <DatePicker
          label="End Date"
          value={end}
          format="MM/dd/yyyy"
          onChange={(next) => onChange({ startDate, endDate: next?.toISODate() ?? undefined })}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
      </Stack>
    </LocalizationProvider>
  );
}

/** Loading placeholder for the (suspense-driven) generator status tile body. */
function GeneratorStatusSkeleton() {
  return (
    <Stack spacing={1}>
      <Skeleton height={48} variant="rounded" />
      <Skeleton height={36} variant="rounded" />
    </Stack>
  );
}

/**
 * Reportable waste streams subsection. Composes the shared chrome-free dashboard
 * grid body (fixed to the waste-streams view — the dashboard's "View by" toggle
 * is omitted here) under the step's consistent `StepSubHeader`. The enclosing
 * `TenantWasteFacilityProvider` bounds it to the activity window and the
 * `Reportable` view, so it lists only the streams included in the submission.
 */
function WasteStreamsSection() {
  return (
    <Stack spacing={2}>
      <StepSubHeader
        title="Reportable Waste Streams"
        infoTooltip={<WasteStreamsInfoTooltip />}
        action={<WasteStreamsDateRangeCaption />}
      />
      <WasteStreamsBody selectedGraphType="waste-streams" readOnly fitContent />
    </Stack>
  );
}

/**
 * Consolidated RCRA Waste Activity stepper step. Combines the former Generator
 * Status and Waste Streams steps into one screen, plus a read-only summary of
 * the facility's reportable waste codes.
 *
 * Layout: the two compact summaries — Generator Status and Reportable waste codes
 * — sit side by side, with the taller reportable Waste Streams grid full-width
 * below to save vertical space.
 *
 * A single user-adjustable date window (default: trailing 6 months) bounds the
 * Generator Status (reported lookback + calculated status) and the reportable
 * Waste Streams grid (via `TenantWasteFacilityProvider`). The Reportable waste
 * codes section is intentionally NOT bound to this window; it always shows the
 * all-time reportable set that matches the snapshot.
 *
 * The step is strictly read-only: the embedded waste streams grid is rendered
 * without its edit affordances, and a single CTA at the top links out to the
 * waste facility page where all edits happen. This avoids duplicating the
 * facility page's edit surfaces (and its destructive actions, like merging
 * waste streams) inside the stepper.
 */
export function WasteActivityStep({
  submissionId,
  facilityId,
}: {
  submissionId: string;
  facilityId: string;
}) {
  const { tenantId = "" } = useParams<{ tenantId: string }>();
  const { startDate, endDate, setRange } = useAdjustableStepWasteDateRange();

  const handleDateRangeChange = (next: { startDate?: string; endDate?: string }) => {
    // Intentionally ignore cleared/partial ranges (one side missing): the activity
    // window must always stay bounded, so a reset leaves the current range in place
    // rather than unbounding the sections.
    if (next.startDate && next.endDate) {
      setRange({ startDate: next.startDate, endDate: next.endDate });
    }
  };

  return (
    <Stack spacing={3}>
      <StepHeader title="Waste Activity" />

      {/* icon={false}: design feedback — the icon eats horizontal space in
          this small inline note. */}
      <Alert severity="info" icon={false}>
        This step is a read-only summary of the facility&apos;s waste activity. To make changes, use
        the waste facility page — updates are reflected here.{" "}
        <AmendInWasteOverviewLink tenantId={tenantId} facilityId={facilityId} />
      </Alert>

      <Stack spacing={1}>
        <Typography variant="caption" color="text.secondary">
          Activity window — governs the generator status and reportable waste streams below.
        </Typography>
        <ActivityDateRange
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateRangeChange}
        />
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="stretch">
        <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 0 }}>
          <Stack spacing={2}>
            <StepSubHeader title="Generator Status" />
            <Suspense fallback={<GeneratorStatusSkeleton />}>
              <WasteGeneratorStatus
                facilityId={facilityId}
                startDate={startDate}
                endDate={endDate}
              />
            </Suspense>
          </Stack>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 0 }}>
          <ReportableWasteCodes facilityId={facilityId} />
        </Paper>
      </Stack>

      <Suspense fallback={<Skeleton height={510} variant="rounded" />}>
        <TenantWasteFacilityProvider
          facilityId={facilityId}
          startDate={startDate}
          endDate={endDate}
          view={FacilityWasteView.Reportable}
        >
          <WasteStreamsSection />
        </TenantWasteFacilityProvider>
      </Suspense>

      <StepContinueFooter
        submissionId={submissionId}
        step={SubmissionStepType.RcraWasteActivity}
        acknowledge
      />
    </Stack>
  );
}
